import { profileTypeMapping } from './profileTypeMapping';
import { 
  CompanyPosition, 
  ResolvedProfile, 
  TraitFitBreakdown, 
  PositionAnalysisResult,
  PositionResult,
  PositionMemberResult
} from '@/types/positionAnalysisTypes';
import { sigmaToPercentile } from './personalityCalculations';

export const standardProfileToRanges = (discCode: string): ResolvedProfile => {
  const profileInfo = profileTypeMapping[discCode as keyof typeof profileTypeMapping];
  const profileName = profileInfo?.type || 'Unknown';
  
  const baseCode = discCode.split('-')[0];
  
  const getRange = (letter: string): [number, number] => {
    return letter === 'H' ? [60, 100] : [0, 40];
  };
  
  const [dRange, eRange, pRange, fRange] = [
    getRange(baseCode[0]),
    getRange(baseCode[1]),
    getRange(baseCode[2]),
    getRange(baseCode[3]),
  ];
  
  return {
    name: profileName,
    dominance_min: dRange[0],
    dominance_max: dRange[1],
    extraversion_min: eRange[0],
    extraversion_max: eRange[1],
    patience_min: pRange[0],
    patience_max: pRange[1],
    formality_min: fRange[0],
    formality_max: fRange[1]
  };
};

export const getTraitFitBreakdown = (
  scores: { dominance: number; extraversion: number; patience: number; formality: number },
  profile: ResolvedProfile
): TraitFitBreakdown[] => {
  const traits = [
    { key: 'dominance', label: 'Drive' },
    { key: 'extraversion', label: 'Connection' },
    { key: 'patience', label: 'Task' },
    { key: 'formality', label: 'Order' },
  ];

  return traits.map(({ key, label }) => {
    const actual = scores[key as keyof typeof scores];
    const min = profile[`${key}_min` as keyof ResolvedProfile] as number;
    const max = profile[`${key}_max` as keyof ResolvedProfile] as number;
    
    const isInRange = actual >= min && actual <= max;
    let deviation = 0;
    
    if (!isInRange) {
      if (actual < min) {
        deviation = min - actual;
      } else {
        deviation = actual - max;
      }
    }

    return {
      trait: label,
      actual,
      idealMin: min,
      idealMax: max,
      isInRange,
      deviation
    };
  });
};

export const calculateProfileRangeFit = (
  scores: { dominance: number; extraversion: number; patience: number; formality: number },
  profile: ResolvedProfile
): number => {
  const breakdown = getTraitFitBreakdown(scores, profile);
  
  const traitFits = breakdown.map(trait => {
    if (trait.isInRange) {
      return 100;
    }
    
    const penalty = trait.deviation * 1.5;
    return Math.max(0, 100 - penalty);
  });
  
  const averageFit = traitFits.reduce((sum, fit) => sum + fit, 0) / traitFits.length;
  return Math.round(averageFit);
};

export const generatePositionAnalysis = (positions: CompanyPosition[]): PositionAnalysisResult => {
  const positionResults: PositionResult[] = [];
  let totalMembers = 0;
  let totalFitSum = 0;

  positions.forEach(position => {
    if (!position.resolvedProfile || position.assignedMembers.length === 0) {
      return;
    }

    const memberResults: PositionMemberResult[] = position.assignedMembers.map(member => {
      console.log('Position Analysis Debug:', {
        memberId: member.memberId,
        memberName: member.memberName,
        rawScores: member.scores,
        dominance: member.scores?.dominance,
        extraversion: member.scores?.extraversion,
        patience: member.scores?.patience,
        formality: member.scores?.formality
      });

      // Use pre-computed percentiles if available (M3), otherwise convert from sigma
      const scores = member.scoresArePercentiles ? {
        dominance: Math.round(member.scores.dominance),
        extraversion: Math.round(member.scores.extraversion),
        patience: Math.round(member.scores.patience),
        formality: Math.round(member.scores.formality),
      } : {
        dominance: sigmaToPercentile(member.scores.dominance ?? 0),
        extraversion: sigmaToPercentile(member.scores.extraversion ?? 0),
        patience: sigmaToPercentile(member.scores.patience ?? 0),
        formality: sigmaToPercentile(member.scores.formality ?? 0),
      };

      const fitPercentage = calculateProfileRangeFit(scores, position.resolvedProfile!);
      const traitBreakdown = getTraitFitBreakdown(scores, position.resolvedProfile!);

      totalFitSum += fitPercentage;
      totalMembers++;

      return {
        memberId: member.memberId,
        memberName: member.memberName,
        email: member.email,
        fitPercentage,
        traitBreakdown
      };
    });

    const averageFit = memberResults.length > 0
      ? Math.round(memberResults.reduce((sum, m) => sum + m.fitPercentage, 0) / memberResults.length)
      : 0;

    positionResults.push({
      positionId: position.id,
      positionName: position.name,
      profileName: position.resolvedProfile.name,
      members: memberResults,
      averageFit
    });
  });

  const overallCompanyFit = totalMembers > 0 
    ? Math.round(totalFitSum / totalMembers)
    : 0;

  return {
    positions: positionResults,
    overallCompanyFit,
    totalMembers,
    totalPositions: positionResults.length
  };
};
